
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Grid, MenuItem, TextField, Button, CircularProgress,
  Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Divider, IconButton, Tooltip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemText, ListItemSecondaryAction, Badge,
} from '@mui/material';
import {
  SportsSoccer, Add, CheckCircle, Cancel, Delete, Warning,
  WifiOff, Sync, Storage, Close, AccessTime, Groups,
} from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../../api/apiClient';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG        = '#f4f7f5';
const SURFACE   = '#ffffff';
const BORDER    = '#d6e4de';
const ACCENT    = '#1e6b45';
const ACCENT_BG = '#e6f2ec';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';
const WARN_BG   = '#fff8e1';
const WARN_BOR  = '#ffe082';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: SURFACE, color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiSelect-icon': { color: TEXT_SEC },
};

const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: SURFACE, border: `1px solid ${BORDER}`, borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(30,107,69,0.10)',
      '& .MuiMenuItem-root': {
        color: TEXT_PRI,
        '&:hover': { bgcolor: ACCENT_BG },
        '&.Mui-selected': { bgcolor: ACCENT_BG, color: ACCENT },
        '&.Mui-selected:hover': { bgcolor: ACCENT_BG },
      },
    },
  },
};

const addBtnSx = (online) => ({
  bgcolor: online ? ACCENT : '#b45309',
  color: '#ffffff', borderRadius: '8px', textTransform: 'none', fontWeight: 600,
  boxShadow: '0 4px 12px rgba(30,107,69,0.25)',
  '&:hover': { bgcolor: online ? '#175c39' : '#92400e', boxShadow: '0 6px 16px rgba(30,107,69,0.35)', color: '#ffffff' },
  '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
});

// ─── IndexedDB helpers ────────────────────────────────────────────────────────
const DB_NAME    = 'pwa_penalties_db';
const DB_VERSION = 1;
const STORE_NAME = 'pending_penalties';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME))
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function savePendingPenalty(gameId, penalty) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE_NAME).add({ gameId, penalty, savedAt: Date.now() });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function getPendingPenaltiesByGame(gameId) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve((req.result || []).filter((r) => r.gameId === gameId));
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function deletePendingPenaltyById(id) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE_NAME).delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function deletePendingPenaltiesByGame(gameId) {
  const db     = await openDB();
  const tx     = db.transaction(STORE_NAME, 'readwrite');
  const store  = tx.objectStore(STORE_NAME);
  const allReq = store.getAll();
  return new Promise((resolve, reject) => {
    allReq.onsuccess = () => {
      const toDelete = (allReq.result || []).filter((r) => r.gameId === gameId);
      let remaining  = toDelete.length;
      if (remaining === 0) { resolve(); return; }
      toDelete.forEach((item) => {
        const del = store.delete(item.id);
        del.onsuccess = () => { if (--remaining === 0) resolve(); };
        del.onerror   = (e) => reject(e.target.error);
      });
    };
    allReq.onerror = (e) => reject(e.target.error);
  });
}

const isOnline = () => navigator.onLine;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getUniquePlayers = (players = []) => {
  const map = new Map();
  players.forEach(({ entityId, userIds }) => {
    (userIds || []).forEach((u) => {
      if (!map.has(u._id)) map.set(u._id, { ...u, entityId });
    });
  });
  return [...map.values()];
};

const fmtTime = (ts) =>
  new Date(ts).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

// ─── OfflinePenaltiesDialog ───────────────────────────────────────────────────
const OfflinePenaltiesDialog = ({
  open, onClose, pendingRows, allPlayers, entities,
  onDelete, onDeleteAll, syncing, onSync, online,
}) => {
  const playerMap = useMemo(() => {
    const m = new Map(); allPlayers.forEach((p) => m.set(p._id, p)); return m;
  }, [allPlayers]);

  const entityMap = useMemo(() => {
    const m = new Map(); entities.forEach((e) => m.set(e._id, e)); return m;
  }, [entities]);

  const resolvePlayer = (id) => {
    const p = playerMap.get(id);
    return p
      ? `${p.firstName} ${p.lastName}${p.actualJerseyNumber ? ` #${p.actualJerseyNumber}` : ''}`
      : id || '—';
  };
  const resolveEntity = (id) => entityMap.get(id)?.name || id || '—';

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px', border: `1px solid ${WARN_BOR}`,
          bgcolor: SURFACE, boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        bgcolor: WARN_BG, borderBottom: `1px solid ${WARN_BOR}`,
        py: 1.5, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Storage fontSize="small" sx={{ color: '#b45309' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#92400e' }}>
            Penalties hors-ligne ({pendingRows.length})
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: TEXT_SEC }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        {pendingRows.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: TEXT_SEC }}>Aucun penalty en attente.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {pendingRows.map((row, idx) => {
              const { penalty, savedAt, id } = row;
              return (
                <React.Fragment key={id ?? idx}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      px: 2, py: 1.25,
                      borderBottom: idx < pendingRows.length - 1 ? `1px solid ${BORDER}` : 'none',
                      '&:hover': { bgcolor: BG },
                      transition: 'background 0.15s',
                    }}
                  >
                    {/* Index badge */}
                    <Box sx={{
                      minWidth: 32, height: 32, borderRadius: '50%',
                      bgcolor: WARN_BG, border: `1px solid ${WARN_BOR}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mr: 1.5, mt: 0.25, flexShrink: 0,
                    }}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: '#b45309' }}>
                        {idx + 1}
                      </Typography>
                    </Box>

                    <ListItemText
                      disableTypography
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                          {/* Minute */}
                          <Chip
                            icon={<AccessTime sx={{ fontSize: '0.75rem !important' }} />}
                            label={`${penalty.minute}'`}
                            size="small"
                            sx={{ bgcolor: ACCENT_BG, color: ACCENT, fontWeight: 700, border: `1px solid ${BORDER}`, fontSize: '0.72rem' }}
                          />
                          {/* Goal scored */}
                          {penalty.goalScored
                            ? <Chip icon={<CheckCircle sx={{ fontSize: '0.8rem !important' }} />} label="Marqué" size="small" color="success" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                            : <Chip icon={<Cancel sx={{ fontSize: '0.8rem !important' }} />}      label="Raté"   size="small" color="error"   sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                          }
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                          {/* Player */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SportsSoccer sx={{ fontSize: '0.85rem', color: TEXT_SEC }} />
                            <Typography variant="caption" sx={{ color: TEXT_PRI, fontWeight: 600 }}>
                              {resolvePlayer(penalty.userId)}
                            </Typography>
                          </Box>
                          {/* Team */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Groups sx={{ fontSize: '0.85rem', color: TEXT_SEC }} />
                            <Typography variant="caption" sx={{ color: TEXT_SEC }}>
                              {resolveEntity(penalty.entityId)}
                            </Typography>
                          </Box>
                          {/* Saved at */}
                          <Typography variant="caption" sx={{ color: '#b45309', fontSize: '0.67rem', mt: 0.2 }}>
                            Sauvegardé le {fmtTime(savedAt)}
                          </Typography>
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <Tooltip title="Supprimer ce penalty hors-ligne">
                        <IconButton
                          edge="end" size="small" onClick={() => onDelete(id)}
                          sx={{ color: '#c62828', bgcolor: '#fdecea', borderRadius: '6px', '&:hover': { bgcolor: '#f9cbc8' } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>

      {/* Footer */}
      {pendingRows.length > 0 && (
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${BORDER}`, justifyContent: 'space-between' }}>
          <Button
            size="small" variant="outlined" color="error"
            startIcon={<Delete fontSize="small" />}
            onClick={onDeleteAll}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
          >
            Tout supprimer
          </Button>
          {online && (
            <Button
              size="small" variant="contained" disabled={syncing}
              onClick={onSync}
              startIcon={syncing ? <CircularProgress size={13} color="inherit" /> : <Sync fontSize="small" />}
              sx={{ bgcolor: ACCENT, borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: '#fff', '&:hover': { bgcolor: '#175c39' } }}
            >
              {syncing ? 'Synchronisation…' : 'Synchroniser maintenant'}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};



// ─── Main component ───────────────────────────────────────────────────────────
const PenaltiesTab = ({ game, onRefresh }) => {
  const [form, setForm]             = useState({ entityId: '', userId: '', goalScored: 'false', minute: '' });
  const [loading, setLoading]       = useState(false);
  const [syncing, setSyncing]       = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId]   = useState(null);
  const [error, setError]           = useState('');
  const [snack, setSnack]           = useState({ open: false, message: '', severity: 'success' });
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingRows, setPendingRows]   = useState([]);
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [online, setOnline]         = useState(isOnline());
  const [canAddPenalty, setCanAddPenalty] = useState(false);

  const syncLockRef = useRef(false);

  // ── Refresh pending list ──
  const refreshPending = useCallback(async () => {
    try {
      const rows = await getPendingPenaltiesByGame(game._id);
      setPendingRows(rows);
      setPendingCount(rows.length);
    } catch {}
  }, [game._id]);

  // ── Sync to server ──
  const syncPendingPenalties = useCallback(async () => {
    if (syncLockRef.current || !isOnline()) return;
    syncLockRef.current = true;
    setSyncing(true);
    try {
      const pending = await getPendingPenaltiesByGame(game._id);
      if (pending.length === 0) { setSyncing(false); syncLockRef.current = false; return; }

      await apiCall((headers) =>
        axios.post(
          `${BASE_URL}/games/${game._id}/in-game-penalties/bulk`,
          { penalties: pending.map((r) => r.penalty) },
          { headers }
        )
      );

      await deletePendingPenaltiesByGame(game._id);
      setPendingRows([]);
      setPendingCount(0);
      setDialogOpen(false);
      setSnack({ open: true, message: `${pending.length} penalty(ies) synchronisé(s) avec succès !`, severity: 'success' });
      onRefresh();
    } catch {
      setSnack({ open: true, message: 'Échec de la synchronisation, réessai ultérieur…', severity: 'warning' });
    } finally {
      setSyncing(false);
      syncLockRef.current = false;
    }
  }, [game._id, onRefresh]);

  // ── Delete single pending ──
  const handleDeletePending = useCallback(async (id) => {
    try { await deletePendingPenaltyById(id); await refreshPending(); } catch {}
  }, [refreshPending]);

  // ── Delete all pending ──
  const handleDeleteAllPending = useCallback(async () => {
    try {
      await deletePendingPenaltiesByGame(game._id);
      setPendingRows([]); setPendingCount(0); setDialogOpen(false);
    } catch {}
  }, [game._id]);

  // ── Open dialog ──
  const handleOpenDialog = useCallback(async () => {
    await refreshPending(); setDialogOpen(true);
  }, [refreshPending]);

  // ── Online/offline listeners ──
  useEffect(() => {
    const handleOnline  = () => { setOnline(true);  syncPendingPenalties(); };
    const handleOffline = () => setOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    refreshPending();
    if (isOnline()) syncPendingPenalties();
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingPenalties, refreshPending]);

  // // ── Permission check ──
  // useEffect(() => {
  //   const currentUser = (() => {
  //     try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
  //   })();
  //   if (!currentUser) return;
  //   if (currentUser.role === 'entityPlatformAdmin') { setCanAddPenalty(true); return; }
  //   const ownerEntry = (game?.eventOwnersAndRoles || []).find((e) => e.userId?._id === currentUser._id);
  //   if (!ownerEntry) return;
  //   const roles = ownerEntry.roles || [];
  //   setCanAddPenalty(roles.includes('eventSuperAdmin') || roles.includes('eventGameUpdater'));
  // }, [game?._id]);

// ── Permission check ──
useEffect(() => {
  const currentUser = (() => {
    try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
  })();
  if (!currentUser) return;
  const ownerEntry = (game?.eventOwnersAndRoles || []).find((e) => e.userId?._id === currentUser._id);
  if (!ownerEntry) return;
  setCanAddPenalty(true);
}, [game?._id]);

  // ── Derived data ──
  const entities = useMemo(() => {
    const map = new Map();
    (game?.players || []).forEach(({ entityId }) => { if (entityId?._id) map.set(entityId._id, entityId); });
    return [...map.values()];
  }, [game]);

  const allPlayers = useMemo(() => getUniquePlayers(game?.players || []), [game]);

  const playersForEntity = useMemo(() => {
    if (!form.entityId) return [];
    return allPlayers.filter((p) => p.entityId?._id === form.entityId);
  }, [form.entityId, allPlayers]);

  const handleChange       = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const handleEntityChange = (e) => setForm((p) => ({ ...p, entityId: e.target.value, userId: '' }));

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.entityId || !form.userId || !form.minute) {
      setError('Équipe, Joueur et Minute sont obligatoires'); return;
    }
    setLoading(true); setError('');

    const penaltyPayload = {
      entityId:   form.entityId,
      userId:     form.userId,
      goalScored: form.goalScored === 'true',
      minute:     Number(form.minute),
    };

    if (isOnline()) {
      try {
        await apiCall((headers) =>
          axios.post(`${BASE_URL}/games/${game._id}/in-game-penalties`, penaltyPayload, { headers })
        );
        setSnack({ open: true, message: 'Penalty ajouté avec succès !', severity: 'success' });
        setForm({ entityId: '', userId: '', goalScored: 'false', minute: '' });
        onRefresh();
      } catch (err) {
        setError(err.response?.data?.message || 'Échec de l\'ajout du penalty');
      }
    } else {
      try {
        await savePendingPenalty(game._id, penaltyPayload);
        await refreshPending();
        setSnack({
          open: true,
          message: 'Hors-ligne — penalty sauvegardé localement, sera synchronisé à la reconnexion.',
          severity: 'warning',
        });
        setForm({ entityId: '', userId: '', goalScored: 'false', minute: '' });
      } catch {
        setError('Impossible de sauvegarder le penalty localement');
      }
    }
    setLoading(false);
  };

  const handleDeleteRequest = (penaltyId) => { setConfirmId(penaltyId); setError(''); };
  const handleDeleteCancel  = () => setConfirmId(null);

  const handleDeleteConfirm = async () => {
    const penaltyId = confirmId;
    setConfirmId(null); setDeletingId(penaltyId); setError('');
    try {
      await apiCall((headers) =>
        axios.delete(`${BASE_URL}/games/${game._id}/in-game-penalties/${penaltyId}`, { headers })
      );
      setDeletingId(null);
      setSnack({ open: true, message: 'Penalty supprimé avec succès !', severity: 'success' });
      onRefresh();
    } catch (err) {
      setDeletingId(null);
      setError(err.response?.data?.message || 'Échec de la suppression du penalty');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Offline / pending banner ── */}
      {(!online || pendingCount > 0) && (
        <Alert
          severity={online ? 'info' : 'warning'}
          icon={online ? <Sync fontSize="small" /> : <WifiOff fontSize="small" />}
          sx={{ mb: 1.5, borderRadius: '10px' }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {pendingCount > 0 && (
                <Badge badgeContent={pendingCount} color="warning" overlap="rectangular">
                  <Button
                    size="small" variant="outlined"
                    onClick={handleOpenDialog}
                    startIcon={<Storage fontSize="small" />}
                    sx={{
                      textTransform: 'none', fontWeight: 600,
                      borderColor: WARN_BOR, color: '#92400e', borderRadius: '7px',
                      '&:hover': { bgcolor: WARN_BG, borderColor: '#f59e0b' },
                    }}
                  >
                    Voir
                  </Button>
                </Badge>
              )}
              {online && pendingCount > 0 && (
                <Button
                  size="small" onClick={syncPendingPenalties} disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={12} color="inherit" /> : <Sync fontSize="small" />}
                  sx={{ textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                >
                  Synchroniser
                </Button>
              )}
            </Box>
          }
        >
          {!online && pendingCount === 0 && 'Hors-ligne — les penalties seront sauvegardés localement.'}
          {!online && pendingCount > 0  && `Hors-ligne — ${pendingCount} penalty(ies) en attente de synchronisation.`}
          {online  && pendingCount > 0  && `${pendingCount} penalty(ies) en attente de synchronisation.`}
        </Alert>
      )}

      {/* ── Add Penalty form ── */}
      {canAddPenalty && (
        <>
          <Typography variant="subtitle2" gutterBottom sx={{ color: TEXT_PRI, fontWeight: 700 }}>
            Ajouter un penalty
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 1, borderRadius: '10px' }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Équipe"
                value={form.entityId} onChange={handleEntityChange}
                sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
                {entities.map((e) => <MenuItem key={e._id} value={e._id}>{e.name}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Joueur"
                value={form.userId} onChange={handleChange('userId')}
                disabled={!form.entityId} sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
                {playersForEntity.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {p.firstName} {p.lastName}{p.actualJerseyNumber ? ` #${p.actualJerseyNumber}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Minute" type="number"
                value={form.minute} onChange={handleChange('minute')}
                inputProps={{ min: 1, max: 120 }} sx={inputSx} />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="But marqué ?"
                value={form.goalScored} onChange={handleChange('goalScored')}
                sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
                <MenuItem value="true">Oui ✓</MenuItem>
                <MenuItem value="false">Non ✗</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained" size="small" onClick={handleSubmit} disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={14} color="inherit" />
                          : online ? <Add /> : <WifiOff fontSize="small" />
                }
                sx={addBtnSx(online)}
              >
                {online ? 'Ajouter un penalty' : 'Sauvegarder hors-ligne'}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 1.5, borderColor: BORDER }} />
        </>
      )}

      {/* ── Penalties list ── */}
      <Typography variant="subtitle2" gutterBottom sx={{ color: TEXT_PRI, fontWeight: 700 }}>
        Penalties ({game?.inGamePenalties?.length || 0})
      </Typography>

      {game?.inGamePenalties?.length > 0 ? (
        <TableContainer component={Paper} elevation={0}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', bgcolor: SURFACE, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Joueur', 'Équipe', 'Minute', 'Marqué', 'Action'].map((label) => (
                  canAddPenalty || label !== 'Action' ? (
                    <TableCell
                      key={label}
                      align={['Marqué', 'Action'].includes(label) ? 'center' : 'left'}
                      sx={{
                        color: TEXT_SEC, fontWeight: 700, fontSize: '0.68rem',
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        borderBottom: `1px solid ${BORDER}`, py: 1.5, bgcolor: BG,
                      }}
                    >
                      {label}
                    </TableCell>
                  ) : null
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {game.inGamePenalties.map((p, i) => {
                const isConfirming = confirmId  === p._id;
                const isDeleting   = deletingId === p._id;

                return (
                  <React.Fragment key={p._id || i}>
                    <TableRow hover sx={{
                      '&:hover': { bgcolor: ACCENT_BG },
                      '& td': { borderColor: BORDER },
                      ...(isConfirming && { bgcolor: '#fff5f5', opacity: 0.9 }),
                    }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <SportsSoccer fontSize="small" sx={{ color: TEXT_SEC }} />
                          <Typography variant="body2" sx={{ color: TEXT_PRI }}>
                            {p.userId?.firstName} {p.userId?.lastName}
                            {p.userId?.actualJerseyNumber ? ` #${p.userId.actualJerseyNumber}` : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: TEXT_PRI }}>{p.entityId?.name || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: ACCENT }}>{p.minute}'</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {p.goalScored
                          ? <Chip icon={<CheckCircle />} label="Marqué" size="small" color="success" sx={{ fontWeight: 600 }} />
                          : <Chip icon={<Cancel />}      label="Raté"   size="small" color="error"   sx={{ fontWeight: 600 }} />
                        }
                      </TableCell>
                      {canAddPenalty && (
                        <TableCell align="center">
                          {isDeleting ? (
                            <CircularProgress size={16} sx={{ color: ACCENT }} />
                          ) : isConfirming ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Confirmer la suppression">
                                <IconButton size="small" color="error" onClick={handleDeleteConfirm}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Annuler">
                                <IconButton size="small" onClick={handleDeleteCancel} sx={{ color: TEXT_SEC }}>
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Tooltip title="Supprimer">
                              <IconButton size="small" onClick={() => handleDeleteRequest(p._id)}
                                sx={{ color: '#c62828', bgcolor: '#fdecea', borderRadius: '6px', '&:hover': { bgcolor: '#f9cbc8' } }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>

                    {canAddPenalty && (
                      <TableRow sx={{ p: 0 }}>
                        <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                          <Collapse in={isConfirming} unmountOnExit>
                            <Alert severity="warning" icon={<Warning fontSize="small" />}
                              sx={{
                                borderRadius: 0, py: 0.5, px: 2, bgcolor: '#fff8e1',
                                '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1, width: '100%' },
                              }}
                              action={
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button size="small" variant="contained" color="error"
                                    onClick={handleDeleteConfirm}
                                    startIcon={<Delete fontSize="small" />}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: '#ffffff' }}>
                                    Supprimer
                                  </Button>
                                  <Button size="small" variant="outlined" onClick={handleDeleteCancel}
                                    sx={{ textTransform: 'none', borderRadius: '8px', color: TEXT_SEC, borderColor: BORDER }}>
                                    Annuler
                                  </Button>
                                </Box>
                              }
                            >
                              <Typography variant="body2" sx={{ color: TEXT_PRI }}>
                                Confirmer la suppression de ce penalty ?
                              </Typography>
                            </Alert>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" sx={{ color: TEXT_SEC }}>Aucun penalty pour l'instant</Typography>
      )}

      {/* ── Offline Penalties Dialog ── */}
      <OfflinePenaltiesDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        pendingRows={pendingRows}
        allPlayers={allPlayers}
        entities={entities}
        onDelete={handleDeletePending}
        onDeleteAll={handleDeleteAllPending}
        syncing={syncing}
        onSync={syncPendingPenalties}
        online={online}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{
            borderRadius: '10px',
            ...(snack.severity === 'success' && {
              bgcolor: ACCENT_BG, color: ACCENT,
              border: `1px solid ${BORDER}`,
              '& .MuiAlert-icon': { color: ACCENT },
            }),
          }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PenaltiesTab;
